import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2,
  Eye,
  EyeOff,
  X,
  Save,
  GripVertical,
  Package,
  Trophy,
  Gift,
  Maximize2,
  Minimize2,
  Settings,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { productTypesAPI } from '../../utils/api';

// Icon mapping for display
const ICON_MAP = {
  Trophy: Trophy,
  Package: Package,
  Gift: Gift,
  Maximize2: Maximize2,
  Minimize2: Minimize2,
};

const AVAILABLE_ICONS = ['Trophy', 'Package', 'Gift', 'Maximize2', 'Minimize2'];

// ==============================================
// PRODUCT TYPE CARD
// ==============================================
const ProductTypeCard = ({ productType, onEdit, onDelete, onToggleActive, onDragStart, onDragOver, onDrop }) => {
  const IconComponent = ICON_MAP[productType.ui?.icon] || Package;
  
  return (
    <div
      className={`bg-white rounded-xl border shadow-soft overflow-hidden transition-all ${
        !productType.is_active ? 'opacity-60 border-ivory-300' : 'border-ivory-200 hover:shadow-md'
      }`}
      draggable
      onDragStart={(e) => onDragStart(e, productType)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, productType)}
      data-testid={`product-type-card-${productType.slug}`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="cursor-grab hover:cursor-grabbing p-1 text-ivory-400 hover:text-ivory-600">
            <GripVertical className="w-5 h-5" />
          </div>
          
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            productType.is_active ? 'bg-gold-100 text-gold-600' : 'bg-ivory-100 text-ivory-400'
          }`}>
            <IconComponent className="w-6 h-6" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-heading font-semibold text-ivory-800 truncate">
                {productType.name}
              </h3>
              {!productType.is_active && (
                <span className="text-xs bg-ivory-200 text-ivory-500 px-2 py-0.5 rounded">
                  Inactive
                </span>
              )}
            </div>
            <p className="text-sm text-ivory-500 font-body">
              Slug: <code className="bg-ivory-100 px-1 rounded">{productType.slug}</code>
            </p>
          </div>
          
          <div className="flex items-center gap-1">
            <button 
              onClick={() => onToggleActive(productType)}
              className="p-2 rounded-lg hover:bg-ivory-100 text-ivory-500 hover:text-gold-600 transition-colors"
              title={productType.is_active ? 'Deactivate' : 'Activate'}
            >
              {productType.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button 
              onClick={() => onEdit(productType)}
              className="p-2 rounded-lg hover:bg-ivory-100 text-ivory-500 hover:text-gold-600 transition-colors"
              title="Edit"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button 
              onClick={() => onDelete(productType)}
              className="p-2 rounded-lg hover:bg-red-50 text-ivory-500 hover:text-red-600 transition-colors"
              title="Delete"
              disabled={productType.product_count > 0 || productType.category_count > 0}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="mt-4 grid grid-cols-3 gap-3">
          <div className="bg-ivory-50 rounded-lg p-3 text-center">
            <p className="text-lg font-heading font-semibold text-ivory-800">{productType.product_count || 0}</p>
            <p className="text-xs text-ivory-500 font-body">Products</p>
          </div>
          <div className="bg-ivory-50 rounded-lg p-3 text-center">
            <p className="text-lg font-heading font-semibold text-ivory-800">{productType.category_count || 0}</p>
            <p className="text-xs text-ivory-500 font-body">Categories</p>
          </div>
          <div className="bg-ivory-50 rounded-lg p-3 text-center">
            <p className="text-lg font-heading font-semibold text-ivory-800">{productType.sort_order}</p>
            <p className="text-xs text-ivory-500 font-body">Order</p>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-ivory-100">
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="bg-ivory-100 text-ivory-600 px-2 py-1 rounded font-body">
              Route: {productType.ui?.default_route}
            </span>
            {productType.settings?.requires_artwork && (
              <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded font-body">
                Requires Artwork
              </span>
            )}
            {productType.settings?.markup_multiplier && (
              <span className="bg-green-100 text-green-600 px-2 py-1 rounded font-body">
                {productType.settings.markup_multiplier}x Markup
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==============================================
// PRODUCT TYPE MODAL
// ==============================================
const ProductTypeModal = ({ productType, onSave, onClose }) => {
  const isEdit = !!productType?.id;
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: productType?.name || '',
    slug: productType?.slug || '',
    sort_order: productType?.sort_order || 0,
    is_active: productType?.is_active ?? true,
    ui: {
      label_singular: productType?.ui?.label_singular || '',
      label_plural: productType?.ui?.label_plural || '',
      icon: productType?.ui?.icon || 'Package',
      default_route: productType?.ui?.default_route || '',
    },
    settings: {
      requires_artwork: productType?.settings?.requires_artwork ?? false,
      production_days_default: productType?.settings?.production_days_default || 5,
      markup_multiplier: productType?.settings?.markup_multiplier || null,
    },
  });

  // Auto-generate slug from name
  const handleNameChange = (value) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: isEdit ? prev.slug : value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
      ui: {
        ...prev.ui,
        label_singular: prev.ui.label_singular || value,
        label_plural: prev.ui.label_plural || value,
        default_route: isEdit ? prev.ui.default_route : `/${value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`,
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return alert('Name is required');
    if (!formData.slug.trim()) return alert('Slug is required');
    
    setSaving(true);
    try {
      if (isEdit) {
        await productTypesAPI.adminUpdateProductType(productType.id, formData);
      } else {
        await productTypesAPI.adminCreateProductType(formData);
      }
      onSave();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const IconPreview = ICON_MAP[formData.ui.icon] || Package;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-ivory-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gold-100 rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-gold-600" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold text-ivory-800">
                {isEdit ? 'Edit Product Type' : 'Add Product Type'}
              </h3>
              {isEdit && (
                <p className="text-sm text-ivory-500 font-body">Slug cannot be changed after creation</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-ivory-100">
            <X className="w-5 h-5 text-ivory-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h4 className="font-heading font-medium text-ivory-700 border-b border-ivory-200 pb-2">Basic Information</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1 font-body">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 font-body focus:outline-none focus:ring-2 focus:ring-gold-500"
                  placeholder="e.g., Awards"
                  required
                  data-testid="product-type-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1 font-body">
                  Slug * {isEdit && <span className="text-ivory-400">(locked)</span>}
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => !isEdit && setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  className={`w-full px-4 py-2 rounded-lg border border-ivory-300 font-body focus:outline-none focus:ring-2 focus:ring-gold-500 ${
                    isEdit ? 'bg-ivory-200 text-ivory-500 cursor-not-allowed' : 'bg-ivory-50 text-ivory-800'
                  }`}
                  placeholder="e.g., awards"
                  required
                  disabled={isEdit}
                  data-testid="product-type-slug-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1 font-body">Sort Order</label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 font-body focus:outline-none focus:ring-2 focus:ring-gold-500"
                  min="0"
                />
              </div>
              
              <div className="flex items-center gap-3 pt-6">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-ivory-300 text-gold-500"
                />
                <label htmlFor="is_active" className="text-sm text-ivory-700 font-body">
                  Active (visible on frontend)
                </label>
              </div>
            </div>
          </div>
          
          {/* UI Settings */}
          <div className="space-y-4">
            <h4 className="font-heading font-medium text-ivory-700 border-b border-ivory-200 pb-2">UI Settings</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1 font-body">Label (Singular)</label>
                <input
                  type="text"
                  value={formData.ui.label_singular}
                  onChange={(e) => setFormData({ ...formData, ui: { ...formData.ui, label_singular: e.target.value } })}
                  className="w-full px-4 py-2 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 font-body focus:outline-none focus:ring-2 focus:ring-gold-500"
                  placeholder="e.g., Award"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1 font-body">Label (Plural)</label>
                <input
                  type="text"
                  value={formData.ui.label_plural}
                  onChange={(e) => setFormData({ ...formData, ui: { ...formData.ui, label_plural: e.target.value } })}
                  className="w-full px-4 py-2 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 font-body focus:outline-none focus:ring-2 focus:ring-gold-500"
                  placeholder="e.g., Awards"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1 font-body">Icon</label>
                <div className="flex gap-2">
                  <select
                    value={formData.ui.icon}
                    onChange={(e) => setFormData({ ...formData, ui: { ...formData.ui, icon: e.target.value } })}
                    className="flex-1 px-4 py-2 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 font-body focus:outline-none focus:ring-2 focus:ring-gold-500"
                  >
                    {AVAILABLE_ICONS.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                  <div className="w-10 h-10 bg-gold-100 rounded-lg flex items-center justify-center">
                    <IconPreview className="w-5 h-5 text-gold-600" />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1 font-body">Default Route</label>
                <input
                  type="text"
                  value={formData.ui.default_route}
                  onChange={(e) => setFormData({ ...formData, ui: { ...formData.ui, default_route: e.target.value } })}
                  className="w-full px-4 py-2 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 font-body focus:outline-none focus:ring-2 focus:ring-gold-500"
                  placeholder="e.g., /awards"
                />
              </div>
            </div>
          </div>
          
          {/* Business Settings */}
          <div className="space-y-4">
            <h4 className="font-heading font-medium text-ivory-700 border-b border-ivory-200 pb-2">Business Settings</h4>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="requires_artwork"
                  checked={formData.settings.requires_artwork}
                  onChange={(e) => setFormData({ ...formData, settings: { ...formData.settings, requires_artwork: e.target.checked } })}
                  className="w-4 h-4 rounded border-ivory-300 text-gold-500"
                />
                <label htmlFor="requires_artwork" className="text-sm text-ivory-700 font-body">
                  Requires Artwork
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1 font-body">Production Days</label>
                <input
                  type="number"
                  value={formData.settings.production_days_default}
                  onChange={(e) => setFormData({ ...formData, settings: { ...formData.settings, production_days_default: parseInt(e.target.value) || 5 } })}
                  className="w-full px-4 py-2 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 font-body focus:outline-none focus:ring-2 focus:ring-gold-500"
                  min="1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1 font-body">Markup Multiplier</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.settings.markup_multiplier || ''}
                  onChange={(e) => setFormData({ ...formData, settings: { ...formData.settings, markup_multiplier: e.target.value ? parseFloat(e.target.value) : null } })}
                  className="w-full px-4 py-2 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 font-body focus:outline-none focus:ring-2 focus:ring-gold-500"
                  placeholder="e.g., 4.0"
                />
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 justify-end pt-4 border-t border-ivory-200">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="btn-luxury-primary" disabled={saving} data-testid="product-type-save-btn">
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />{isEdit ? 'Update' : 'Create'}</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==============================================
// MAIN COMPONENT
// ==============================================
const AdminProductTypes = () => {
  const [loading, setLoading] = useState(true);
  const [productTypes, setProductTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);

  const fetchProductTypes = async () => {
    setLoading(true);
    try {
      const response = await productTypesAPI.adminGetProductTypes();
      setProductTypes(response.product_types || []);
    } catch (err) {
      console.error('Error fetching product types:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductTypes();
  }, []);

  const handleToggleActive = async (productType) => {
    try {
      await productTypesAPI.adminToggleProductTypeActive(productType.id);
      fetchProductTypes();
    } catch (err) {
      alert('Failed to toggle: ' + err.message);
    }
  };

  const handleDelete = async (productType) => {
    if (productType.product_count > 0 || productType.category_count > 0) {
      return alert('Cannot delete product type with existing products or categories. Move or delete them first.');
    }
    if (!window.confirm(`Delete product type "${productType.name}"?`)) return;
    
    try {
      await productTypesAPI.adminDeleteProductType(productType.id);
      fetchProductTypes();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, productType) => {
    setDraggedItem(productType);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetType) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetType.id) return;
    
    // Reorder logic
    const newTypes = [...productTypes];
    const draggedIndex = newTypes.findIndex(t => t.id === draggedItem.id);
    const targetIndex = newTypes.findIndex(t => t.id === targetType.id);
    
    newTypes.splice(draggedIndex, 1);
    newTypes.splice(targetIndex, 0, draggedItem);
    
    // Update sort orders
    const orders = newTypes.map((t, idx) => ({ id: t.id, sort_order: idx + 1 }));
    
    try {
      await productTypesAPI.adminReorderProductTypes(orders);
      fetchProductTypes();
    } catch (err) {
      alert('Failed to reorder: ' + err.message);
    }
    
    setDraggedItem(null);
  };

  const totalProducts = productTypes.reduce((sum, pt) => sum + (pt.product_count || 0), 0);
  const totalCategories = productTypes.reduce((sum, pt) => sum + (pt.category_count || 0), 0);
  const activeTypes = productTypes.filter(pt => pt.is_active).length;

  return (
    <div className="space-y-6" data-testid="admin-product-types">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-ivory-800">Product Types</h1>
          <p className="text-sm text-ivory-500 font-body mt-1">
            Manage product types - add new types without code changes
          </p>
        </div>
        <Button 
          onClick={() => { setEditingType(null); setShowModal(true); }}
          className="btn-luxury-primary rounded-lg font-body"
          data-testid="add-product-type-btn"
        >
          <Plus className="w-4 h-4 mr-2" />Add Product Type
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-ivory-200 p-4">
          <p className="text-2xl font-heading font-semibold text-ivory-800">{productTypes.length}</p>
          <p className="text-sm text-ivory-500 font-body">Total Types</p>
        </div>
        <div className="bg-white rounded-xl border border-ivory-200 p-4">
          <p className="text-2xl font-heading font-semibold text-green-600">{activeTypes}</p>
          <p className="text-sm text-ivory-500 font-body">Active</p>
        </div>
        <div className="bg-white rounded-xl border border-ivory-200 p-4">
          <p className="text-2xl font-heading font-semibold text-gold-600">{totalProducts}</p>
          <p className="text-sm text-ivory-500 font-body">Total Products</p>
        </div>
        <div className="bg-white rounded-xl border border-ivory-200 p-4">
          <p className="text-2xl font-heading font-semibold text-blue-600">{totalCategories}</p>
          <p className="text-sm text-ivory-500 font-body">Total Categories</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
        <div>
          <p className="text-sm text-blue-800 font-body">
            <strong>Data-Driven Configuration:</strong> Add a new product type here and it will automatically appear in:
          </p>
          <ul className="text-sm text-blue-700 font-body mt-1 ml-4 list-disc">
            <li>Frontend navigation and product tabs</li>
            <li>Admin product filters and category manager</li>
            <li>API endpoints for filtering</li>
          </ul>
        </div>
      </div>

      {/* Product Types Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
        </div>
      ) : productTypes.length === 0 ? (
        <div className="bg-white rounded-xl border border-ivory-200 p-12 text-center">
          <Package className="w-12 h-12 text-ivory-300 mx-auto mb-4" />
          <p className="text-ivory-500 font-body">No product types found</p>
          <p className="text-sm text-ivory-400 font-body mt-1">Click &quot;Add Product Type&quot; to create one</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {productTypes.map(pt => (
            <ProductTypeCard
              key={pt.id}
              productType={pt}
              onEdit={(t) => { setEditingType(t); setShowModal(true); }}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            />
          ))}
        </div>
      )}

      {showModal && (
        <ProductTypeModal
          productType={editingType}
          onSave={() => { setShowModal(false); setEditingType(null); fetchProductTypes(); }}
          onClose={() => { setShowModal(false); setEditingType(null); }}
        />
      )}
    </div>
  );
};

export default AdminProductTypes;
